import { apiBPConfirm } from '@api/apiCall';
import BPTnc from '@atoms/BPTnc';
import BPTnCHeader from '@atoms/BPTnCHeader';
import { Button, View } from '@components/primitives';
import { AntDesign } from '@expo/vector-icons';
import { useToast } from '@hooks';
import BPTncAndBillDetails from '@molecules/BPTncAndBillDetails';
import navigationConstants from '@navigation/navigationConstants';
import { useNavigation } from '@react-navigation/native';
import appStyles from '@root/appStyles';
import { getStore } from '@store/storeUtils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import colors from '@theme/colors';
import AmplitudeHelper from '@utils/analytics';
import { AMPLITUDE_CONSTANTS } from '@utils/analytics/constants';
import { getAmplitudeKeyValue, showErrorOnAPI } from '@utils/utils';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, TouchableOpacity } from 'react-native';
import {
  logErrorAnalytics,
  logSheetOpened,
  logTnCClosed,
} from '@molecules/RequestTransactionSheet/analyticsRequestTxSheet';
import { PaymentMethod } from '@molecules/RequestTransactionSheet/types';
import { logTermsPage } from '@molecules/BPTncAndBillDetails/analyticsTransaction';
import styles from './styles';

interface PaymentSummaryProps {
  route: {
    params: {
      bpReqData: any;
      productConfig: any;
      source: string;
      productApiData: any;
      selectedAccountNumber: string;
      nominalSelected: any;
      selectedBPAccountForConfirm?: PaymentMethod;
    };
  };
}

const getMutationConfig = () => ({
  mutationFn: (payload: any) => apiBPConfirm(payload),
});

const logAmplitudeEvent = (
  eventName: any,
  productConfig: any,
  bpReqData: any,
  selectedBPAccountForConfirm: any,
  source: string,
  validatedReferralCode: string,
  metaParams: Record<string, any> = {},
) => {
  const { name, attributes } = eventName;
  AmplitudeHelper.logEvent(name, {
    [attributes.transaction_category_id]: productConfig?.transaction_category_id,
    [attributes.product_price]: bpReqData?.subtotal,
    [attributes.product_code]: bpReqData?.productCode,
    [attributes.reason]: productConfig?.reason,
    [attributes.reason_id]: productConfig?.reasonId,
    [attributes.payment_method]: selectedBPAccountForConfirm?.amplitudeAccountName,
    [attributes.source]: source,
    [attributes.fee_charged]: bpReqData?.fee,
    [attributes.referral_code]: validatedReferralCode,
    ...metaParams,
  });
};

function PaymentSummary({ route }: PaymentSummaryProps) {
  const { params } = route;
  const {
    bpReqData,
    productConfig,
    source,
    productApiData,
    selectedAccountNumber,
    nominalSelected,
    selectedBPAccountForConfirm: initialSelectedBPAccount,
  } = params;
  const navigation = useNavigation<any>();
  const { t } = useTranslation('common');
  const toast = useToast();
  const queryClient = useQueryClient();

  const [tncChecked, setTncChecked] = useState(false);
  const [tncOpen, setTncOpen] = useState(false);
  const [selectedBPAccountForConfirm, setSelectedBPAccountForConfirm] = useState<PaymentMethod>(
    initialSelectedBPAccount || ({ id: 0, amplitudeAccountName: '' } as PaymentMethod),
  );

  const {
    validatedReferralCode,
    setLoading,
    setInitialData,
    selectedBPAccount,
    setValidatedReferralCode,
    meAmplitudeContext,
  } = getStore();

  const { isLoading, isError, isSuccess, error, data, mutate: confirmBP } = useMutation(getMutationConfig());

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (isError && error) {
      const errorArray = Array.isArray(error) ? error : (error as any)?.response?.data?.error || [];
      if (errorArray.length > 0) {
        logErrorAnalytics(productConfig, bpReqData, t, errorArray, selectedBPAccountForConfirm, source);
      }
      showErrorOnAPI(isError, errorArray, toast, t, navigation, setInitialData, validatedReferralCode || '', source);
    }
  }, [isError, error]);

  useEffect(() => {
    if (isSuccess && data) {
      queryClient.invalidateQueries({ queryKey: ['transactions_history'] });

      const e1 = AMPLITUDE_CONSTANTS.BillPayment.selamatPage;
      const e2 = AMPLITUDE_CONSTANTS.BillPayment.BillPaymentSuccess;

      logAmplitudeEvent(e1, productConfig, bpReqData, selectedBPAccountForConfirm, source, validatedReferralCode);

      logAmplitudeEvent(
        e2,
        productConfig,
        bpReqData,
        selectedBPAccountForConfirm,
        source,
        validatedReferralCode,
        getAmplitudeKeyValue(e2.name, (data as any)?.amplitudeEventContext),
      );

      setValidatedReferralCode(null);
      // Using replace() instead of navigate() to remove PaymentSummary from the navigation stack
      // This ensures users cannot go back to PaymentSummary from the success page
      navigation.replace(navigationConstants.GG_BP_SUCCESS, {
        bpRequestSuccess: data,
        bpReqData,
        productConfig,
        selectedBPAccountForConfirm,
        source,
        productApiData,
      });
    }
  }, [isSuccess, data]);

  useEffect(() => {
    if (
      bpReqData?.paymentMethods?.length &&
      selectedBPAccount &&
      'id' in selectedBPAccount &&
      selectedBPAccount.id !== undefined
    ) {
      const matched = bpReqData.paymentMethods.find((item: any) => item.id === selectedBPAccount.id);
      if (matched) {
        setSelectedBPAccountForConfirm(matched as PaymentMethod);
      }
    }
  }, [bpReqData, selectedBPAccount]);

  useEffect(() => {
    logSheetOpened(selectedAccountNumber, productConfig, bpReqData, source, validatedReferralCode);
  }, []);

  useEffect(() => {
    if (tncOpen) {
      logTermsPage(productConfig, source, validatedReferralCode);
    }
  }, [tncOpen]);

  const closeTnCModal = () => {
    logTnCClosed(productConfig, source);
    setTncOpen(false);
  };

  const handleConfirmBP = () => {
    if (
      !isLoading &&
      tncChecked &&
      selectedBPAccountForConfirm?.id !== undefined &&
      selectedBPAccountForConfirm?.id !== null
    ) {
      const accountId =
        typeof selectedBPAccountForConfirm.id === 'string'
          ? parseInt(selectedBPAccountForConfirm.id, 10)
          : selectedBPAccountForConfirm.id;

      confirmBP({
        providerId: bpReqData.id,
        employeeAccountNumber: accountId,
        description: '',
        referralCode: validatedReferralCode || null,
        donatorCount: null,
        donatorName: null,
        transactionId: bpReqData.id,
      });
    }
  };

  const accountName = selectedBPAccountForConfirm?.amplitudeAccountName || '';

  const selectedBPAccountForDetails = selectedBPAccountForConfirm?.id
    ? {
        id:
          typeof selectedBPAccountForConfirm.id === 'string'
            ? parseInt(selectedBPAccountForConfirm.id, 10)
            : selectedBPAccountForConfirm.id,
        amplitudeAccountName: selectedBPAccountForConfirm.amplitudeAccountName || '',
      }
    : { id: 0, amplitudeAccountName: '' };

  return (
    <View style={{ backgroundColor: colors.neutral.cotton, flex: 1 }}>
      <View style={{ flex: 1 }}>
        <BPTncAndBillDetails
          isLoading={isLoading}
          tncOpen={false}
          productConfig={productConfig}
          props={{
            bpReqData,
            productConfig,
            productApiData,
            selectedAccountNumber,
            source,
            nominalSelected,
            isGold: false,
            errorHandler: () => {},
          }}
          bpReqData={bpReqData}
          tncChecked={tncChecked}
          setOpenRTS={() => {}}
          validatedReferralCode={validatedReferralCode}
          selectedBPAccount={selectedBPAccountForDetails}
          setTncOpen={setTncOpen}
          setTncChecked={setTncChecked}
          confirmBP={handleConfirmBP}
          setSelectedBPAccount={account => {
            setSelectedBPAccountForConfirm({
              id: account.id,
              amplitudeAccountName: account.amplitudeAccountName,
            } as PaymentMethod);
          }}
          source={source}
          meAmplitudeContext={meAmplitudeContext}
        />
      </View>

      <Modal
        visible={tncOpen}
        animationType="none"
        presentationStyle="overFullScreen"
        transparent
        onRequestClose={closeTnCModal}>
        <TouchableOpacity style={styles.modalBackdrop} activeOpacity={1} onPress={closeTnCModal}>
          <TouchableOpacity style={styles.modalContent} activeOpacity={1} onPress={e => e.stopPropagation()}>
            <View style={styles.modalHeaderContainer}>
              <View style={styles.modalHeaderRow}>
                <BPTnCHeader />
                <TouchableOpacity onPress={closeTnCModal} style={styles.modalCloseButton}>
                  <AntDesign name="close" size={20} color={colors.neutral.charcoal} />
                </TouchableOpacity>
              </View>
            </View>
            <View>
              <BPTnc
                setTncChecked={setTncChecked}
                setTncOpen={setTncOpen}
                transactionCategoryId={productConfig.transaction_category_id}
                accountName={accountName}
                source={source}
              />
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </View>
  );
}

export default PaymentSummary;
